import React, { useState, useCallback, useMemo } from 'react';
import { useObservable } from 'react-use-observable';

import { Alert, Row } from 'antd';

import MyCollapse from 'components/Collapse';
import type { Action, GroupModule } from 'v2/interfaces/access-control';
import eduzzAccountsService from 'v2/services/eduzzAccount';

import Checkbox from './Checkbox';
import { AccessPolicyModules } from '../../constants';

const ACTION_NAMES = {
  VIEW: 'visualizar',
  EXPORT: 'exportar',
  SEE_CLIENT_DATA: 'ver dados clientes',
  HIDE_VALUES: 'ocultar valores'
} as const;

const FULLNAMES = {
  INDICATORS: 'myeduzz:meus-contratos:visualizar-indicadores',
  HIDE_VALUES: 'myeduzz:meus-contratos:ocultar-valores',
  DASHBOARD: 'myeduzz:meus-contratos:dashboard',
  ANALYTICS_VIEW: 'myeduzz:analytics:visualizar',
  NUTROR_ADMIN: 'myeduzz:nutror:adm',
  BLINKET_EVENTS_VIEW: 'myeduzz:blinket_events:visualizar',
  BLINKET_EVENTS_ADMIN: 'myeduzz:blinket_events:adm',
  BLINKET_PARTICIPANTS_VIEW: 'myeduzz:blinket_participants:visualizar',
  BLINKET_PARTICIPANTS_ADMIN: 'myeduzz:blinket_participants:adm'
} as const;

type Props = {
  initialOpen: boolean;
  module: GroupModule;
  handleActionsChange: (actionId: number) => void;
  selectedActionsId: number[];
};

const actionNameIncludes = (action: Action, keyword: string) => action.name.toLowerCase().includes(keyword);

const actionNameEquals = (action: Action, keyword: string) => action.name.toLowerCase() === keyword;

export default function FormGroupModule({
  initialOpen = false,
  module,
  handleActionsChange,
  selectedActionsId
}: Props) {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);

  const moduleActions = module.actions;

  const isBetaSubscriptionsDashboard = eduzzAccountsService.verifyUserHasBetaModule('vertical-assinaturas');
  const isBetaNewConnect = eduzzAccountsService.verifyUserHasBetaModule('new_connect');
  const hasBetaSubscriptionsDashboard = isBetaSubscriptionsDashboard || !!user?.is_clube_black;
  const showNewConnectAlert = user?.producer && isBetaNewConnect && module.id === AccessPolicyModules.CONNECT;

  const isActionSelected = useCallback(
    (actionId?: number) => actionId != null && selectedActionsId.includes(actionId),
    [selectedActionsId]
  );

  const getActionIdByFullName = useCallback(
    (fullName: string) => moduleActions.find(a => a.fullname === fullName)?.id,
    [moduleActions]
  );

  const isSelectedByFullname = useCallback(
    (fullName: string) => isActionSelected(getActionIdByFullName(fullName)),
    [getActionIdByFullName, isActionSelected]
  );

  const findActionByName = useCallback(
    (keyword: string) => moduleActions.find(a => a.name.toLowerCase().includes(keyword)),
    [moduleActions]
  );

  const viewAction = useMemo(
    () => moduleActions.find(a => actionNameIncludes(a, ACTION_NAMES.VIEW) && a.fullname !== FULLNAMES.INDICATORS),
    [moduleActions]
  );

  const shouldDisableBlinketAction = useCallback(
    (action: Action) => {
      const eventsViewSelected = isSelectedByFullname(FULLNAMES.BLINKET_EVENTS_VIEW);
      const eventsAdminSelected = isSelectedByFullname(FULLNAMES.BLINKET_EVENTS_ADMIN);
      const participantsViewSelected = isSelectedByFullname(FULLNAMES.BLINKET_PARTICIPANTS_VIEW);
      const participantsAdminSelected = isSelectedByFullname(FULLNAMES.BLINKET_PARTICIPANTS_ADMIN);

      switch (action.fullname) {
        case FULLNAMES.BLINKET_EVENTS_ADMIN:
          return !eventsViewSelected;

        case FULLNAMES.BLINKET_PARTICIPANTS_ADMIN:
          return !participantsViewSelected || !eventsViewSelected;

        case FULLNAMES.BLINKET_EVENTS_VIEW:
          return eventsViewSelected && (eventsAdminSelected || participantsAdminSelected || participantsViewSelected);

        case FULLNAMES.BLINKET_PARTICIPANTS_VIEW:
          if (!eventsViewSelected) return true;
          return participantsAdminSelected && participantsViewSelected;

        default:
          return false;
      }
    },
    [isSelectedByFullname]
  );

  const shouldDisableExportAction = useCallback(() => {
    const seeClientDataAction = findActionByName(ACTION_NAMES.SEE_CLIENT_DATA);
    const hideValuesAction = findActionByName(ACTION_NAMES.HIDE_VALUES);

    if (!hideValuesAction || !seeClientDataAction?.status) return false;

    const seeClientDataSelected = isActionSelected(seeClientDataAction.id);
    const hideValuesSelected = isActionSelected(hideValuesAction.id);

    return !seeClientDataSelected || hideValuesSelected;
  }, [findActionByName, isActionSelected]);

  const shouldDisableClientDataOrHideValues = useCallback(() => {
    const exportAction = findActionByName(ACTION_NAMES.EXPORT);
    return exportAction ? isActionSelected(exportAction.id) : false;
  }, [findActionByName, isActionSelected]);

  const shouldDisableViewAction = useCallback(() => {
    if (!viewAction) return false;

    const isViewSelected = isActionSelected(viewAction.id);
    if (!isViewSelected) return false;

    const selectedActions = moduleActions.filter(a => isActionSelected(a.id));
    const onlyViewSelected = selectedActions.length === 1 && selectedActions[0].id === viewAction.id;

    return !onlyViewSelected;
  }, [viewAction, moduleActions, isActionSelected]);

  const shouldDisableAction = useCallback(
    (action: Action) => {
      const isBlinketAction = action.fullname.includes('blinket');

      if (isBlinketAction) {
        return shouldDisableBlinketAction(action);
      }

      const isIndicatorsAction = action.fullname === FULLNAMES.INDICATORS;
      const isHideValuesAction = action.fullname === FULLNAMES.HIDE_VALUES;

      if (isHideValuesAction && isSelectedByFullname(FULLNAMES.INDICATORS)) {
        return true;
      }

      if (isIndicatorsAction) {
        const isViewActionSelected = viewAction && isActionSelected(viewAction.id);
        return !isViewActionSelected || isSelectedByFullname(FULLNAMES.HIDE_VALUES);
      }

      if (action.fullname === FULLNAMES.NUTROR_ADMIN) {
        return false;
      }

      if (!viewAction) {
        return false;
      }

      const hasAnyActionSelected = moduleActions.some(
        a => isActionSelected(a.id) && a.fullname !== FULLNAMES.INDICATORS
      );
      const isViewAction = actionNameIncludes(action, ACTION_NAMES.VIEW) && action.fullname !== FULLNAMES.INDICATORS;

      if (!hasAnyActionSelected) {
        return !isViewAction;
      }

      if (actionNameEquals(action, ACTION_NAMES.EXPORT)) {
        return shouldDisableExportAction();
      }

      const isSeeClientDataAction = actionNameEquals(action, ACTION_NAMES.SEE_CLIENT_DATA);

      if (isSeeClientDataAction || isHideValuesAction) {
        return shouldDisableClientDataOrHideValues();
      }

      if (isViewAction) {
        return shouldDisableViewAction();
      }

      return false;
    },
    [
      shouldDisableBlinketAction,
      isSelectedByFullname,
      viewAction,
      moduleActions,
      isActionSelected,
      shouldDisableExportAction,
      shouldDisableClientDataOrHideValues,
      shouldDisableViewAction
    ]
  );

  const onActionsChange = useCallback(
    (action: Action, checked: boolean) => {
      const isSelectingViewAction = checked && actionNameEquals(action, ACTION_NAMES.VIEW);
      const seeClientDataAction = isSelectingViewAction ? findActionByName(ACTION_NAMES.SEE_CLIENT_DATA) : null;

      if (seeClientDataAction) handleActionsChange(seeClientDataAction.id);
      handleActionsChange(action.id);
    },
    [findActionByName, handleActionsChange]
  );

  const visibleActions = useMemo(
    () => moduleActions.filter(action => action.fullname !== FULLNAMES.DASHBOARD || hasBetaSubscriptionsDashboard),
    [moduleActions, hasBetaSubscriptionsDashboard]
  );

  return (
    <>
      <div className='mb-2' />

      <MyCollapse title={module.name} arrow contentActive={isOpen} onClick={() => setIsOpen(prev => !prev)}>
        <Row gutter={[16, 16]}>
          {visibleActions.map(action => (
            <React.Fragment key={action.id}>
              <Checkbox
                module={module}
                isChecked={isActionSelected(action.id)}
                isDisabled={shouldDisableAction(action)}
                action={action}
                handleActionsChange={onActionsChange}
              />
              {action.fullname === FULLNAMES.ANALYTICS_VIEW && (
                <Alert
                  type='warning'
                  message='Todos os dados do Analytics serão exibidos para o usuário ao liberar o acesso visualizar.'
                  style={{ width: '100%' }}
                />
              )}
            </React.Fragment>
          ))}
        </Row>
        {showNewConnectAlert && (
          <Row gutter={[16, 16]}>
            <Alert
              type='warning'
              message='O controle de acesso funcionará apenas para a versão anterior do connect, estamos trabalhando para implementar essa funcionalidade para nova versão em breve.'
              style={{ width: '100%' }}
            />
          </Row>
        )}
      </MyCollapse>
    </>
  );
}
